import React, { useCallback, useMemo, useState } from "react";

import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import ChatOutlined from "@mui/icons-material/ChatOutlined";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import { IconButton, Stack, TableCell, TableCellProps, TableRow, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatDateHook, useModals } from "hooks";
import { useGetDealByIdQuery, useGetDealInvestorsQuery } from "queries/query-selectors/tagger-query-selectors";
import { useTaggerActions, useTaggerDocumentArraySelector } from "store";
import { TagTableRow } from "store/types";

import { PsychologyReverse } from "../../../assets/material-custom-icons";
import { TagMethod } from "../TableDrawer/TagTable";

import "./TagRow.styles.scss";

interface Props {
  row: TagTableRow;
  onEditTag: TagMethod;
  setRowData: (value: TagTableRow | null) => void;
  setAnchorEl: (value: HTMLElement | null) => void;
  hideFirstCell?: boolean;
  borderLess?: boolean;
  availableTag: boolean;
}
export const TagRow: React.FC<Props> = ({ row, ...restProps }) => {
  const { deleteModal } = useModals();
  const { deleteTag, deleteSplitOfTags } = useTaggerActions();
  const formatDate = useFormatDateHook();
  const allDocs = useTaggerDocumentArraySelector();

  const {
    id,
    children,
    date,
    deal,
    accounting_date,
    is_child,
    measure,
    description,
    investor,
    value,
    currency,
    filename,
    round,
    split_id,
    series_id,
    deleted_at,
    is_draft,
    draft_measure_data_id,
    measure_data_id,
    needs_sync,
    is_analysis,
    is_ai,
    is_splittable
  } = row;

  const documentSeriesId = useMemo(() => {
    return allDocs.find((doc) => doc.dealId === deal.id)?.documentSeriesId || null;
  }, [deal, allDocs]);

  const handleDeleteElement = (data: { dealId: number; seriesId: number | null; tagId: number }) => {
    deleteModal({
      action: async () => {
        deleteTag(data);
      },
      element: "Delete",
      message: "Are you sure you want to delete data?",
      onPressSuccess: "Delete"
    });
  };

  const handleDeleteSplit = (data: {
    dealId: number;
    seriesId: number | null;
    splitId: number;
    investorId?: number;
  }) => {
    deleteModal({
      action: async () => {
        deleteSplitOfTags(data);
      },
      element: "Delete",
      message:
        "This will delete the entire transaction with all the related lines per different investors. Are you sure you want to delete data?",
      onPressSuccess: "Delete"
    });
  };

  const { data: dealData } = useGetDealByIdQuery(deal.id);
  const { data: investorsData } = useGetDealInvestorsQuery(deal.id);

  const rounds = dealData?.rounds || [];

  const investorsLength: number = useMemo(() => {
    if (!investorsData) return 0;
    return investorsData.investors.length;
  }, [investorsData]);

  const { onEditTag, setRowData, setAnchorEl, hideFirstCell, availableTag } = restProps;
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const tagColor: string = useMemo(() => {
    if (is_draft && !draft_measure_data_id) return "#FFA500";
    if (availableTag && needs_sync) return "#496682";
    if (availableTag && !needs_sync) return "#333333";
    return "#92A7BC";
  }, [availableTag, needs_sync, is_draft, draft_measure_data_id]);

  const commonCellStyles: TableCellProps["sx"] = {
    color: tagColor,
    textDecoration: deleted_at ? "line-through" : "none",
    borderBottom: "1px solid #DBE8F5 !important"
  };

  const isMoreThanOneUniqInvestor = useMemo(() => {
    if (!children?.length) return false;

    const uniqueInvestorIds = new Set(children.map((item) => item.investor?.id));
    return uniqueInvestorIds.size > 1;
  }, [children]);

  const isDateMultiple = useMemo(() => {
    if (!children?.length) return false;

    const uniqueDates = new Set(children.map((item) => item.date));
    return uniqueDates.size > 1;
  }, [children]);

  const getAllFilenames = (items: TagTableRow[]): string[] => {
    return items.reduce<string[]>((acc, item) => {
      const filenames = [item.filename];
      if (item.children?.length) {
        filenames.push(...getAllFilenames(item.children));
      }
      return [...acc, ...filenames];
    }, []);
  };

  const isFilesnameMultiple = useMemo(() => {
    if (!children?.length) return false;

    const allFilenames = getAllFilenames(children);
    const uniqueFilenames = new Set(allFilenames);
    return uniqueFilenames.size > 1;
  }, [children]);

  const getIndentation = (row: TagTableRow, hasChildren: boolean) => {
    if (!hideFirstCell) return 0;

    const baseIndent = 5 * row.nested_level;
    const leafNodeIndent = hasChildren ? 0 : 5;

    return baseIndent + leafNodeIndent;
  };

  const onDeleteTag = useCallback(() => {
    if (!children?.length) {
      return handleDeleteElement({
        dealId: deal.id,
        tagId: id,
        seriesId: series_id || documentSeriesId
      });
    }
    if (split_id) {
      return handleDeleteSplit({
        dealId: deal.id,
        seriesId: series_id || documentSeriesId,
        splitId: split_id,
        investorId: investor?.id
      });
    }
  }, [deal.id, id, series_id, documentSeriesId, children, split_id, investor?.id]);

  const isShowMoreVert = useMemo(() => {
    if (draft_measure_data_id && !is_splittable) return false;
    if (measure_data_id && !is_draft && !is_splittable) return false;
    if (!is_child && rounds.length >= 1 && investorsLength > 1 && !deleted_at) return true;
    return false;
  }, [measure_data_id, is_draft, is_splittable, is_child, rounds.length, investorsLength, deleted_at]);

  return (
    <>
      <TableRow>
        <TableCell sx={commonCellStyles}>
          {!!measure_data_id && availableTag && <CheckIcon sx={{ color: "#07A27D" }} />}
          {draft_measure_data_id && <CheckIcon sx={{ color: "#FFA500" }} />}
          {(is_analysis || is_ai) && <PsychologyReverse sx={{ fill: "#07A27D", width: "16px", height: "16px" }} />}
        </TableCell>
        <TableCell
          sx={{ ...commonCellStyles, padding: children?.length ? "6px 0px !important" : "6px 22px !important" }}
        >
          <MaterialTooltip
            isShow={
              measure.name.length >= 18 ||
              (!!children?.length && measure.name.length >= 10 && getIndentation(row, !!children?.length) >= 10)
            }
            title={children?.length ? measure.name + ` (${children?.length})` : measure.name}
            placement="top"
          >
            <Typography
              sx={{ maxWidth: "145px", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}
              variant="body2"
            >
              <Stack
                sx={{
                  gap: "2px",
                  fontSize: "15px",
                  paddingLeft: `${getIndentation(row, !!children?.length)}px`
                }}
                flexDirection="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                {!!children?.length && (
                  <IconButton
                    onClick={() => setIsOpen(!isOpen)}
                    size="sm"
                    sx={{ padding: "0px !important", width: "20px", height: "20px" }}
                  >
                    <ArrowForwardIosOutlinedIcon
                      sx={{
                        fontSize: "10px",
                        transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                        transition: "transform 0.2s"
                      }}
                    />
                  </IconButton>
                )}
                <Typography
                  sx={{
                    maxWidth: "145px",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    display: "block"
                  }}
                >
                  {children?.length ? measure.name + ` (${children?.length})` : measure.name}
                </Typography>
              </Stack>
            </Typography>
          </MaterialTooltip>
        </TableCell>
        {/* )} */}
        <TableCell sx={commonCellStyles}>
          <Typography variant="body2">{Intl.NumberFormat().format(value)}</Typography>
          {/* <Typography variant="body2">{value.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</Typography> */}
        </TableCell>
        <TableCell sx={commonCellStyles}>
          <Typography variant="body2">{currency}</Typography>
        </TableCell>
        <TableCell sx={commonCellStyles}>
          <Typography variant="body2">{isDateMultiple ? "Multiple" : formatDate({ date })}</Typography>
        </TableCell>
        {/* {rounds.length > 1 && ( */}
        <TableCell sx={commonCellStyles}>
          <Typography variant="body2">{round?.name}</Typography>
        </TableCell>
        {/* )} */}
        <TableCell sx={commonCellStyles}>
          <Typography variant="body2">{isMoreThanOneUniqInvestor ? "Multiple" : investor?.name}</Typography>
        </TableCell>
        <TableCell sx={commonCellStyles}>
          <Typography variant="body2">{accounting_date && formatDate({ date: accounting_date })}</Typography>
        </TableCell>
        <TableCell sx={commonCellStyles}>
          <Typography variant="body2">{deal.title}</Typography>
        </TableCell>
        <TableCell sx={commonCellStyles}>
          <MaterialTooltip isShow={filename.length > 20} title={filename} placement="top">
            <Typography
              sx={{ maxWidth: "140px", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}
              variant="body2"
            >
              {isFilesnameMultiple ? "Multiple" : filename}
            </Typography>
          </MaterialTooltip>
        </TableCell>
        <TableCell sx={{ borderBottom: "1px solid #DBE8F5 !important" }}>
          <Stack justifyContent="flex-end" alignItems="center" flexDirection="row">
            <Stack justifyContent="flex-start" alignItems="center" flexDirection="row-reverse" gap="30px">
              {!deleted_at && (
                <IconButton disabled={!availableTag} className="tag-table__icon-btn" onClick={onDeleteTag}>
                  <DeleteOutline className="tag-table__icon" />
                </IconButton>
              )}
              {!deleted_at && (
                <IconButton
                  disabled={!availableTag}
                  className="tag-table__icon-btn"
                  onClick={() => onEditTag(deal, id, !!is_child, !!(children && !!children.length))}
                >
                  <EditOutlinedIcon />
                </IconButton>
              )}
              {isShowMoreVert && (
                <IconButton
                  disabled={!availableTag}
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setRowData(row);
                  }}
                  className="tag-table__icon-btn"
                >
                  <MoreVert className="tag-table__icon" />
                </IconButton>
              )}
              {description && is_child && !children && (
                <MaterialTooltip isShow title={description} placement="top">
                  <ChatOutlined className="tag-table__icon" sx={{ fill: "#0A3142" }} />
                </MaterialTooltip>
              )}
            </Stack>
          </Stack>
        </TableCell>
      </TableRow>
      {isOpen &&
        row?.children?.map((row, index, arr) => (
          <TagRow key={index} {...restProps} row={row} hideFirstCell borderLess={index < arr.length - 1} />
        ))}
    </>
  );
};
